<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { IJobRecord } from '@/interfaces/print-bridge-protocol';
  import {
    formatJobTime,
    jobKindUi,
    jobStateUi,
    useV2PrintingUi,
  } from '@/composition/printing/use-printing-v2-ui';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const ui = useUi();
  const modals = useModal();
  const { bridge, isConnected, printerNameOf, v2Store } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const jobs = computed<IJobRecord[]>(() => bridge.value?.jobs.value ?? []);

  const busyJobIds = ref<string[]>([]);

  function isTerminal(job: IJobRecord): boolean {
    return (
      job.state === 'done' || job.state === 'failed' || job.state === 'unknown'
    );
  }

  /**
   * Retry: only a FAILED job that is certain not to have printed ('safe'),
   * whose bytes this session still holds, and that was NOT an auto-print
   * (auto-prints are retried by the catch-up reconciliation — a raw resend
   * would not update the printed counters and cause a later double print).
   */
  function canRetry(job: IJobRecord): boolean {
    const bridgeStrategy = bridge.value;
    if (!bridgeStrategy || !isConnected.value) return false;
    return (
      job.state === 'failed' &&
      job.error?.retrySafety === 'safe' &&
      bridgeStrategy.hasJobBytes(job.jobId) &&
      !bridgeStrategy.isAutoPrintJob(job.jobId)
    );
  }

  function canReprint(job: IJobRecord): boolean {
    const bridgeStrategy = bridge.value;
    if (!bridgeStrategy || !isConnected.value) return false;
    return isTerminal(job) && bridgeStrategy.hasJobBytes(job.jobId);
  }

  /** Reprint of a possibly-printed document needs a human check first. */
  function needsReprintConfirm(job: IJobRecord): boolean {
    if (job.state === 'unknown') return true;
    return job.state === 'failed' && job.error?.retrySafety !== 'safe';
  }

  /**
   * Retries/reprints target the destination's CURRENT assignment (the user
   * may have reassigned it after the failure — e.g. the printer broke); falls
   * back to the job's original printer when the destination can't be resolved.
   */
  function currentAssignmentRef(job: IJobRecord): string | undefined {
    const doc = v2Store.displayConfig;
    const kind = job.meta?.kind;
    if (!doc || !kind) return undefined;

    let logicalId: number | null | undefined;
    if (kind === 'ticket') {
      logicalId = doc.assignments.ticket;
    } else if (kind === 'sticker') {
      logicalId = doc.assignments.sticker;
    } else if (kind === 'comanda' && job.meta?.stationId != null) {
      logicalId = doc.assignments.comandaByStation[job.meta.stationId];
    }
    if (logicalId == null) return undefined;
    return doc.logicalPrinters.find((lp) => lp.id === logicalId)?.ref;
  }

  async function resubmit(job: IJobRecord, isRetry: boolean) {
    const bridgeStrategy = bridge.value;
    if (!bridgeStrategy || busyJobIds.value.includes(job.jobId)) return;

    if (!isRetry && needsReprintConfirm(job)) {
      const accepted = await modals.confirm({
        title: i18nT('printingV2.reprintConfirmTitle'),
        message: i18nT('printingV2.reprintConfirmBody'),
        acceptText: i18nT('printingV2.reprint'),
      });
      if (!accepted) return;
    }

    busyJobIds.value = [...busyJobIds.value, job.jobId];
    try {
      await bridgeStrategy.resubmitJob(job.jobId, currentAssignmentRef(job));
      ui.showToast(
        AlertType.Success,
        i18nT(isRetry ? 'printingV2.retrySent' : 'printingV2.reprintSent')
      );
    } catch (e) {
      console.error('[PrintingV2] resubmit failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.actionFailed'));
    } finally {
      busyJobIds.value = busyJobIds.value.filter((id) => id !== job.jobId);
    }
  }
</script>

<template>
  <div class="v2-jobs-tab">
    <PosUiEmptyState
      v-if="!jobs.length"
      icon="history"
      :title="$t('printingV2.jobsEmptyTitle')"
      :description="$t('printingV2.jobsEmptyDescription')"
    />

    <div v-else class="jobs-list">
      <div v-for="job in jobs" :key="job.jobId" class="job-row">
        <div class="job-kind-icon">
          <PosUiIcon :name="jobKindUi(job.meta && job.meta.kind).icon" />
        </div>

        <div class="job-info">
          <div class="job-label">
            {{
              (job.meta && job.meta.label) ||
              $t(jobKindUi(job.meta && job.meta.kind).labelKey)
            }}
          </div>
          <div class="job-detail">
            {{ formatJobTime(job.acceptedAt) }} ·
            {{ printerNameOf(job.printerRef) }}
          </div>
          <div v-if="job.state === 'failed' && job.error" class="job-error">
            {{ job.error.message }}
          </div>
        </div>

        <div class="job-side">
          <PosUiBadge
            :color="jobStateUi(job.state).color"
            variant="soft"
            size="sm"
            :icon="jobStateUi(job.state).icon"
          >
            {{ $t(jobStateUi(job.state).labelKey) }}
          </PosUiBadge>

          <div class="job-actions">
            <PosUiButton
              v-if="canRetry(job)"
              size="xs"
              color="primary"
              variant="soft"
              icon="refresh"
              :loading="busyJobIds.includes(job.jobId)"
              @click="resubmit(job, true)"
            >
              {{ $t('printingV2.retry') }}
            </PosUiButton>
            <PosUiButton
              v-else-if="canReprint(job)"
              size="xs"
              color="neutral"
              variant="outline"
              icon="printer"
              :loading="busyJobIds.includes(job.jobId)"
              @click="resubmit(job, false)"
            >
              {{ $t('printingV2.reprint') }}
            </PosUiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-jobs-tab {
    .jobs-list {
      @apply tw-flex tw-flex-col tw-gap-2;

      .job-row {
        @apply tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3;

        .job-kind-icon {
          @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-lg tw-text-neutral-500;
        }

        .job-info {
          @apply tw-min-w-0 tw-flex-1;

          .job-label {
            @apply tw-truncate tw-text-sm tw-font-medium tw-text-neutral-800;
          }

          .job-detail {
            @apply tw-text-xs tw-text-neutral-500;
          }

          .job-error {
            @apply tw-mt-0.5 tw-text-xs tw-text-red-600;
          }
        }

        .job-side {
          @apply tw-flex tw-flex-none tw-flex-col tw-items-end tw-gap-1.5;

          .job-actions {
            @apply tw-flex tw-gap-1;
          }
        }
      }
    }
  }
</style>
